import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, Sparkles } from "lucide-react";
import WellnessSection from "../components/WellnessSection";
import WellnessPaywallPreview from "../components/WellnessPaywallPreview";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Cell,
} from "recharts";

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

function MonthPicker({ value, onChange }) {
  return (
    <input
      data-testid="month-picker"
      type="month"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-white border-2 border-black px-3 py-2 font-mono text-sm"
    />
  );
}

function Metric({ label, value, sub, testid, flag }) {
  return (
    <div
      data-testid={testid}
      className={`p-6 border-r border-b border-black/15 h-full ${
        flag ? "bg-[var(--alert-wash)] border-2 border-[var(--alert-red)] flag-pulse" : "bg-white"
      }`}
    >
      <div className="overline mb-3 flex items-center gap-2">
        {flag && <AlertTriangle size={12} className="text-[var(--alert-red)]" />}
        {label}
      </div>
      <div className={`font-mono font-bold text-3xl ${flag ? "text-[var(--alert-red)]" : "text-black"}`}>{value}</div>
      {sub && <div className="font-mono text-xs text-[var(--text-secondary)] mt-2">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [s, setS] = useState(null);
  const [goals, setGoals] = useState([]);
  const [wellness, setWellness] = useState(null);
  const [wellnessTrend, setWellnessTrend] = useState(null);
  const [coachNote, setCoachNote] = useState("");
  const [loadingNote, setLoadingNote] = useState(false);
  const [insight, setInsight] = useState("");
  const [loadingInsight, setLoadingInsight] = useState(false);

  const load = async () => {
    const requests = [
      api.get("/summary", { params: { month } }),
      api.get("/goals"),
    ];
    if (user?.is_premium) {
      requests.push(api.get("/wellness/summary", { params: { month } }));
    }
    const results = await Promise.all(requests);
    setS(results[0].data);
    setGoals(results[1].data);
    if (user?.is_premium && results[2]) {
      setWellness(results[2].data);
      setLoadingNote(true);
      setCoachNote("");
      api
        .get("/wellness/coach-note", { params: { month } })
        .then((r) => setCoachNote(r.data.note || ""))
        .catch(() => setCoachNote(""))
        .finally(() => setLoadingNote(false));
      api
        .get("/wellness/trend", { params: { months: 6 } })
        .then((r) => setWellnessTrend(r.data))
        .catch(() => setWellnessTrend([]));
    } else {
      setWellness(null);
      setWellnessTrend(null);
      setCoachNote("");
    }
  };

  useEffect(() => { load(); }, [month, user]);

  const getInsight = async () => {
    setLoadingInsight(true);
    setInsight("");
    try {
      const { data } = await api.get("/insights", { params: { month } });
      setInsight(data.insight);
    } finally {
      setLoadingInsight(false);
    }
  };

  const currency = user?.currency || "USD";
  const catData = useMemo(() => {
    if (!s) return [];
    return Object.entries(s.by_category)
      .map(([category, amount]) => {
        const budget = s.category_budgets?.[category] || 0;
        return { category, amount, budget, over: budget > 0 && amount > budget };
      })
      .filter((d) => d.amount > 0 || d.budget > 0)
      .sort((a, b) => b.amount - a.amount);
  }, [s]);

  // Static chart config objects — memoized so recharts doesn't re-render on every parent update
  const chartMargin = useMemo(() => ({ top: 8, right: 8, left: 0, bottom: 0 }), []);
  const axisTickStyle = useMemo(() => ({ fontFamily: "IBM Plex Mono", fontSize: 11 }), []);
  const tooltipContentStyle = useMemo(
    () => ({ fontFamily: "IBM Plex Mono", borderRadius: 0, border: "1px solid #111", background: "#fff" }),
    [],
  );
  const lineDotStyle = useMemo(() => ({ fill: "#111", r: 3 }), []);
  const moneyFormatter = useMemo(() => (v) => fmtMoney(v, currency), [currency]);

  const dailyData = s?.daily || [];

  if (!s) return <div className="font-mono text-sm" data-testid="dashboard-loading">LOADING DATA…</div>;

  const overallOver = s.overall_budget > 0 && s.total > s.overall_budget;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline">CONTROL ROOM / {s.month}</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
            Hello, {user?.name || user?.email?.split("@")[0]}.
          </h1>
          <p className="font-mono text-sm text-[var(--text-secondary)] mt-2">
            Here&apos;s how the month is shaping up.
          </p>
        </div>
        <MonthPicker value={month} onChange={setMonth} />
      </div>

      {/* Flags banner */}
      {s.flags.length > 0 && (
        <div data-testid="flags-banner" className="border-2 border-[var(--alert-red)] bg-[var(--alert-wash)] p-5 flag-pulse">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-[var(--alert-red)] text-white font-display font-black flex items-center justify-center shrink-0">!</div>
            <div className="flex-1">
              <div className="overline text-[var(--alert-red)]">RED FLAG / {s.flags.length} ALERT{s.flags.length > 1 ? "S" : ""}</div>
              <ul className="mt-2 space-y-1 font-mono text-sm text-[var(--alert-red)]">
                {s.flags.map((f, i) => (
                  <li key={`${f.type}-${f.category || "overall"}-${i}`} data-testid={`flag-${i}`}>— {f.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Metrics grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 border-l border-t border-black/15 bg-white">
        <Metric
          testid="metric-income"
          label="INCOME"
          value={fmtMoney(s.income, currency)}
          sub={s.income > 0 ? `${s.savings_rate}% saved` : "Set income"}
        />
        <Metric
          testid="metric-total"
          label="TOTAL SPENT"
          value={fmtMoney(s.total, currency)}
          sub={`${s.daily.length} day${s.daily.length !== 1 ? "s" : ""} active`}
          flag={overallOver}
        />
        <Metric
          testid="metric-savings"
          label="SAVINGS"
          value={fmtMoney(s.savings, currency)}
          sub={s.savings < 0 ? "Spending more than earning" : "Goes toward goals"}
          flag={s.income > 0 && s.savings < 0}
        />
        <Metric
          testid="metric-budget"
          label="MONTHLY BUDGET"
          value={fmtMoney(s.overall_budget, currency)}
          sub={s.overall_budget > 0 ? `${Math.round((s.total / s.overall_budget) * 100)}% used` : "Not set"}
        />
        <Metric
          testid="metric-flags"
          label="RED FLAGS"
          value={s.flags.length}
          sub={s.flags.length === 0 ? "All clear" : "Action needed"}
          flag={s.flags.length > 0}
        />
      </div>

      {/* Wellness section - Premium only */}
      {user?.is_premium ? (
        <WellnessSection
          data={wellness}
          coachNote={coachNote}
          loadingNote={loadingNote}
          trend={wellnessTrend}
        />
      ) : (
        <WellnessPaywallPreview month={month} />
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Category chart */}
        <div className="lg:col-span-2 bg-white border border-black/15 p-6">
          <div className="overline mb-2">SPEND BY CATEGORY</div>
          <h3 className="font-display font-bold text-xl mb-4">Where the money went</h3>
          {catData.length === 0 ? (
            <div className="font-mono text-sm text-[var(--text-secondary)] py-12 text-center">No spending recorded for this month.</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={catData} margin={chartMargin}>
                <CartesianGrid stroke="#11111122" vertical={false} />
                <XAxis dataKey="category" tick={axisTickStyle} stroke="#111" />
                <YAxis tick={axisTickStyle} stroke="#111" />
                <Tooltip
                  contentStyle={tooltipContentStyle}
                  formatter={moneyFormatter}
                />
                <Bar dataKey="amount" radius={0}>
                  {catData.map((d) => (
                    <Cell key={d.category} fill={d.over ? "#FF2B00" : "#111111"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* AI Insight terminal */}
        <div className="bg-[#111111] text-white p-6 border border-black relative overflow-hidden" data-testid="insight-card">
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: "url(https://images.pexels.com/photos/25626445/pexels-photo-25626445.jpeg)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
          <div className="relative">
            <div className="overline text-white/60 flex items-center gap-2">
              <Sparkles size={12} /> AI INSIGHT / TERMINAL
            </div>
            <h3 className="font-display font-bold text-xl mt-2 mb-4">Coach&apos;s read on this month</h3>

            <div className="font-mono text-sm leading-relaxed min-h-[120px] whitespace-pre-wrap">
              {insight ? (
                <>
                  <span className="text-[var(--alert-red)]">█</span> {insight}
                </>
              ) : loadingInsight ? (
                <>
                  <span className="text-[var(--alert-red)] blink">█</span>{" "}
                  <span className="text-white/70">analyzing your spending…</span>
                </>
              ) : (
                <span className="text-white/60">Tap below to generate an insight from your data.</span>
              )}
            </div>

            <button
              data-testid="generate-insight"
              onClick={getInsight}
              disabled={loadingInsight || !user?.is_premium}
              className="mt-4 w-full bg-white text-black py-2 font-display font-bold tracking-wider hover:bg-[var(--alert-red)] hover:text-white transition-colors disabled:opacity-50"
            >
              {!user?.is_premium ? "🔒 PREMIUM — UNLOCK FOR ₹200" : loadingInsight ? "THINKING…" : insight ? "REGENERATE" : "GENERATE INSIGHT"}
            </button>
            {!user?.is_premium && (
              <a
                href="/upgrade"
                data-testid="insight-upgrade-link"
                className="block text-center mt-2 font-mono text-xs text-white/60 underline"
              >
                What&apos;s included with Premium →
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Daily trend */}
      <div className="bg-white border border-black/15 p-6">
        <div className="overline mb-2">DAILY TREND</div>
        <h3 className="font-display font-bold text-xl mb-4">Day-by-day spending</h3>
        {dailyData.length === 0 ? (
          <div className="font-mono text-sm text-[var(--text-secondary)] py-12 text-center">No data yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={dailyData} margin={chartMargin}>
              <CartesianGrid stroke="#11111122" vertical={false} />
              <XAxis dataKey="date" tick={axisTickStyle} stroke="#111" />
              <YAxis tick={axisTickStyle} stroke="#111" />
              <Tooltip
                contentStyle={tooltipContentStyle}
                formatter={moneyFormatter}
              />
              <Line type="monotone" dataKey="amount" stroke="#0047FF" strokeWidth={2} dot={lineDotStyle} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Goals strip */}
      {goals.length > 0 && (
        <div className="bg-white border border-black/15 p-6" data-testid="dashboard-goals">
          <div className="overline mb-2">SAVINGS GOALS</div>
          <h3 className="font-display font-bold text-xl mb-4">Progress toward your targets</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {goals.slice(0, 6).map((g) => {
              const pct = Math.min(100, Math.round((g.current_amount / g.target_amount) * 100));
              const complete = g.current_amount >= g.target_amount;
              return (
                <div key={g.id} className={`border p-4 ${complete ? "border-[var(--success-green)]" : "border-black/15"}`}>
                  <div className="font-display font-bold">{g.name}</div>
                  <div className="font-mono text-xs text-[var(--text-secondary)] mt-1">
                    {fmtMoney(g.current_amount, currency)} / {fmtMoney(g.target_amount, currency)}
                  </div>
                  <div className="h-2 bg-black/10 mt-2">
                    <div className={`h-full ${complete ? "bg-[var(--success-green)]" : "bg-black"}`} style={{ width: `${pct}%` }} />
                  </div>
                  <div className="font-mono text-xs mt-1">{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
